import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_melting-moments-1/artifacts/mfe7l5w4_Screenshot%202026-04-14%20at%208.10.43%20PM.png";
const scrollLinks = ["Collection", "Story", "Experience", "Contact"];
const pageLinks = [{ label: "Mission & Vision", path: "/mission-vision" }];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const sectionMap = {
      Collection: "product-showcase",
      Story: "story-section",
      Experience: "dual-experience",
      Contact: "footer",
    };
    const el = document.getElementById(sectionMap[id]);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <motion.nav
      data-testid="navbar"
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 w-full z-50 transition-all duration-700 ${
        scrolled
          ? "backdrop-blur-xl bg-[#050505]/80 border-b border-[#1F1F1F]"
          : "bg-transparent"
      }`}
    >
      <div className="flex justify-between items-center py-4 px-6 md:px-12 lg:px-24">
        <a
          href="#hero"
          data-testid="navbar-logo"
          className="flex items-center gap-3"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <img src={LOGO_URL} alt="YY Atelier" className="h-10 md:h-12 w-auto object-contain mix-blend-lighten" />
        </a>

        <div className="hidden md:flex items-center gap-10">
          {scrollLinks.map((link) => (
            <button
              key={link}
              data-testid={`nav-link-${link.toLowerCase()}`}
              onClick={() => scrollTo(link)}
              className="font-body text-xs uppercase tracking-[0.2em] text-[#A3A3A3] hover:text-[#C6A96B] transition-colors duration-500 bg-transparent border-none cursor-pointer"
            >
              {link}
            </button>
          ))}
          {pageLinks.map((link) => (
            <button
              key={link.label}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => navigate(link.path)}
              className="font-body text-xs uppercase tracking-[0.2em] text-[#A3A3A3] hover:text-[#C6A96B] transition-colors duration-500 bg-transparent border-none cursor-pointer"
            >
              {link.label}
            </button>
          ))}
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden flex flex-col gap-1.5 bg-transparent border-none cursor-pointer p-2"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <motion.span
            animate={menuOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
            className="block w-6 h-px bg-[#C6A96B]"
          />
          <motion.span
            animate={menuOpen ? { opacity: 0 } : { opacity: 1 }}
            className="block w-6 h-px bg-[#C6A96B]"
          />
          <motion.span
            animate={menuOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
            className="block w-6 h-px bg-[#C6A96B]"
          />
        </button>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden bg-[#050505]/95 backdrop-blur-xl border-t border-[#1F1F1F] overflow-hidden"
          >
            <div className="flex flex-col items-center py-8 gap-6">
              {scrollLinks.map((link, i) => (
                <motion.button
                  key={link}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  onClick={() => scrollTo(link)}
                  className="font-body text-sm uppercase tracking-[0.2em] text-[#A3A3A3] hover:text-[#C6A96B] transition-colors duration-500 bg-transparent border-none cursor-pointer"
                >
                  {link}
                </motion.button>
              ))}
              {pageLinks.map((link, i) => (
                <motion.button
                  key={link.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: (scrollLinks.length + i) * 0.1, duration: 0.5 }}
                  onClick={() => { setMenuOpen(false); navigate(link.path); }}
                  className="font-body text-sm uppercase tracking-[0.2em] text-[#A3A3A3] hover:text-[#C6A96B] transition-colors duration-500 bg-transparent border-none cursor-pointer"
                >
                  {link.label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
